import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@lightbm/shared';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Notice } from '@/components/ui/Notice';
import { useErrorText } from '@/i18n/messages';
import { authApi } from '../api';

/** USR-04: self-service "forgot password" by email. */
export function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const errorText = useErrorText();
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { identifier: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await authApi.forgotPassword(values);
      setSent(true);
    } catch (error) {
      toast.error(errorText(error));
    }
  });

  return (
    <>
      <Link to="/sign-in" className="inline-flex items-center gap-2 font-medium text-primary">
        <ArrowLeft className="size-4" />
        {t('forgot.back')}
      </Link>
      <h1 className="mt-4 text-[30px] leading-tight font-semibold tracking-tight">
        {t('forgot.title')}
      </h1>
      <p className="mt-2 text-[17px] text-ink-2">{t('forgot.subtitle')}</p>

      <div className="mt-8 rounded-[16px] border border-line bg-surface p-6 shadow-card sm:p-8">
        {sent ? (
          <Notice tone="success" title={t('forgot.sentTitle')} role="status">
            {t('forgot.sentText')}
          </Notice>
        ) : (
          <form onSubmit={onSubmit} noValidate className="space-y-5">
            <Field label={t('fields.identifier')} error={errors.identifier} hint={t('forgot.hint')}>
              <Input
                {...register('identifier')}
                autoComplete="username"
                autoCapitalize="none"
                className="h-13 text-[17px]"
              />
            </Field>
            <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
              {t('forgot.submit')}
            </Button>
          </form>
        )}
      </div>
    </>
  );
}
